import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { AlertCircle, CheckCircle, AlertTriangle, HelpCircle, RefreshCw, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ServiceStatusWidget() {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [downdetectorSearch, setDowndetectorSearch] = useState("");

  useEffect(() => {
    fetchServiceStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchServiceStatus, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchServiceStatus = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/external-status`);
      setStatusData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch service status:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "degraded":
      case "partial":
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case "outage":
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <HelpCircle className="w-4 h-4 text-gray-40 dark:text-white0" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      operational: "bg-green-100 text-green-700",
      degraded: "bg-yellow-100 text-yellow-700",
      partial: "bg-yellow-100 text-yellow-700",
      outage: "bg-red-100 text-red-700",
      unknown: "bg-gray-100 text-gray-70 dark:text-white0",
      error: "bg-gray-100 text-gray-70 dark:text-white0"
    };
    return classes[status] || classes.unknown;
  };

  const getStatusText = (status) => {
    const texts = {
      operational: "All Systems Operational",
      degraded: "Performance Issues",
      partial: "Partial Outage",
      outage: "Service Outage",
      unknown: "Status Unknown",
      error: "Unable to fetch status"
    };
    return texts[status] || "Unknown";
  };

  const handleDowndetectorSearch = (e) => {
    e.preventDefault();
    if (downdetectorSearch.trim()) {
      // Create URL-friendly slug from search term
      const slug = downdetectorSearch.toLowerCase().trim().replace(/\s+/g, '-');
      // Open DownDetector in new tab
      window.open(`https://downdetector.com/status/${slug}/`, '_blank');
    }
  };

  if (loading && !statusData) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center justify-between">
            <span>External Service Status</span>
            <RefreshCw className="w-4 h-4 animate-spin text-slate-40 dark:text-white0" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center text-slate-500 dark:text-white py-4">Loading service status...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md" data-testid="service-status-widget">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle>External Service Status</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchServiceStatus}
            disabled={refreshing}
            data-testid="refresh-status-button"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {statusData && (
          <>
            {/* Overall Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(statusData.overall_status)}
                <div>
                  <p className="font-semibold text-slate-80 dark:text-white0">Overall Status</p>
                  <p className="text-sm text-slate-60 dark:text-white0">{getStatusText(statusData.overall_status)}</p>
                </div>
              </div>
              <Badge className={getStatusBadgeClass(statusData.overall_status)}>
                {statusData.overall_status}
              </Badge>
            </div>

            <Separator />

            {/* Microsoft 365 */}
            {statusData.microsoft365 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(statusData.microsoft365.status)}
                    <span className="font-semibold text-slate-80 dark:text-white0">Microsoft 365</span>
                  </div>
                  <Badge className={getStatusBadgeClass(statusData.microsoft365.status)} size="sm">
                    {statusData.microsoft365.status}
                  </Badge>
                </div>
                {statusData.microsoft365.services && (
                  <div className="ml-6 space-y-1 text-sm">
                    {Object.entries(statusData.microsoft365.services).slice(0, 3).map(([name, data]) => (
                      <div key={name} className="flex items-center justify-between text-slate-60 dark:text-white0">
                        <span>{name}</span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(data.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Webex */}
            {statusData.webex && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(statusData.webex.status)}
                    <span className="font-semibold text-slate-80 dark:text-white0">Webex</span>
                  </div>
                  <Badge className={getStatusBadgeClass(statusData.webex.status)} size="sm">
                    {statusData.webex.status}
                  </Badge>
                </div>
                {statusData.webex.services && (
                  <div className="ml-6 space-y-1 text-sm">
                    {Object.entries(statusData.webex.services).slice(0, 3).map(([name, data]) => (
                      <div key={name} className="flex items-center justify-between text-slate-60 dark:text-white0">
                        <span>{name}</span>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(data.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* DownDetector Search */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-slate-60 dark:text-white0" />
                <span className="font-semibold text-slate-80 dark:text-white0">Search DownDetector</span>
              </div>
              <form onSubmit={handleDowndetectorSearch} className="flex gap-2">
                <Input
                  type="text"
                  placeholder="e.g., Microsoft Teams, Zoom, Slack..."
                  value={downdetectorSearch}
                  onChange={(e) => setDowndetectorSearch(e.target.value)}
                  className="flex-1"
                  data-testid="downdetector-search-input"
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="downdetector-search-button"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-xs text-slate-500 dark:text-white ml-1">
                Search for any service on DownDetector.com
              </p>
            </div>

            {/* Last Updated */}
            {lastUpdated && (
              <div className="text-xs text-slate-500 dark:text-white text-center pt-2">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
